import { kebabCase } from "lodash";

export interface ApiMethods {
  title: string;
  description?: string;
  methods: {
    description: string;
    summary: string;
    operationId: string;
    type: string;
    requestBody?: any;
    responses: any;
  }[];
}

export interface ApiSecurity {
  title: string;
  description: string;
  details: {
    type: string;
    scheme: string;
    bearerFormat: string;
  };
}

export interface ApiReference {
  info: {
    title: string;
    description: string;
    version: string;
  };
  security: ApiSecurity[];
  methods: {
    [key: string]: ApiMethods;
  };
}

// Generic types to fall back on when not provided an explicit example
const getGenericTypeExample = (property: any): string | number | boolean | {} => {
  switch (property.type) {
    case "string":
      return property.enum?.length > 0 ? property.enum[0] : "string";
    case "number":
      return 0;
    case "object":
      return {};
    case "boolean":
      return true;
  }
};

/**
 * Get an example value for a given property
 */
function getExample(property: any) {
  if (property.type === "array") {
    // Honour an explicit array-level example when the schema provides one
    // (e.g. `taxIds` renders as ["gst"] rather than a generated placeholder).
    if (property.example !== undefined) {
      return property.example;
    }

    const items = property.items;
    // Only build an object-shaped example when the items are actually an object.
    // Primitive items (string/number/boolean) — even when they carry constraints
    // like `pattern` or `enum` — must render as a primitive, not `{}`.
    const itemIsObject = (items.type === "object" && items.properties) || items.oneOf;
    const item =
      items.example !== undefined || !itemIsObject
        ? getExample(items)
        : formatExampleProperties(items);
    return [item];
  } else {
    return property.example !== undefined ? property.example : getGenericTypeExample(property);
  }
}

/**
 * Format a schema into an example request/response payload json
 */
export function formatExampleProperties(schema: any, oneOfIndex?: number) {
  if (schema.type === "array") {
    // Route arrays through getExample so array-level examples and primitive
    // item handling stay consistent wherever an array schema is encountered.
    return getExample(schema);
  }

  const code = getPropertyKeysForRender(schema.properties || {}).reduce((acc, curr) => {
    const currentProperty = schema.properties[curr];

    if ((currentProperty.type === "object" && currentProperty.properties) || !currentProperty.type) {
      acc[curr] = formatExampleProperties(currentProperty);
    } else {
      acc[curr] = getExample(currentProperty);
    }

    return acc;
  }, {});

  if (schema.oneOf) {
    const oneOfCode = formatExampleProperties(schema.oneOf[oneOfIndex || 0]);

    return {
      ...oneOfCode,
      ...code,
    };
  }

  return code;
}

export function formatNav(schema: any) {
  const methods = Object.keys(schema.methods).map((group) => ({
    label: schema.methods[group].title,
    pages: schema.methods[group].methods.map((method: any) => ({
      label: method.summary,
      url: `#${kebabCase(schema.methods[group].title)}/${kebabCase(method.summary)}`,
    })),
  }));

  return [
    {
      label: "_uncategorized",
      pages: [
        {
          label: "Introduction",
          url: "#introduction",
        },
        {
          label: "Authentication",
          url: "#authentication",
        },
      ],
    },
    ...methods,
  ];
}

// @TODO(kiahn, 2021-04-13) Add proper types for properties
type Property = any;
type Properties = {
  [key: string]: Property;
};
enum PropertyStatus {
  Unpublished = "unpublished",
  Beta = "beta",
}

/*
 * A way to hide unpublished schema changes from the docs
 */
const isPropertyHidden = (property: Property): boolean => {
  return !!property && !!property["x-status"] && property["x-status"] === PropertyStatus.Unpublished;
};

export const isBetaProperty = (property: Property): boolean => {
  return !!property && !!property["x-status"] && property["x-status"] === PropertyStatus.Beta;
};

export const removeItemsOnce = (arr: string[], values: string[]): string[] => {
  for (const value of values) {
    const index: number = arr.indexOf(value);
    if (index > -1) {
      arr.splice(index, 1);
    }
  }

  return arr;
}

export function getEnumValuesForRender(property: Property): string[] {
  let values: string[] = property.items?.enum || property.enum;

  if (property["items"] && property["items"]["x-unpublished"]) {
    values = removeItemsOnce(property["items"]["enum"], property["items"]["x-unpublished"]);
  } else if (property.enum && property["x-unpublished"]) {
    values = removeItemsOnce(property.enum, property["x-unpublished"]);
  }

  return values;
}

/**
 * A list of property keys to render in the docs & code examples
 */
export function getPropertyKeysForRender(properties: Properties): Property[] {
  return Object.keys(properties).filter((propertyKey) => {
    return !isPropertyHidden(properties[propertyKey]);
  });
}

/**
 * Filter an array of objects to render in the docs
 * based on the objects' x-status fields
 */
export function filterUnpublishedElements(array: any[]): any[] {
  return array.filter((el) => el["x-status"]!= "unpublished");
}

/**
 * Convert parameters to a url query string
 */
export function parametersToQueryString(parameters: any[]) {
  if (!parameters || parameters.length < 1) return "";

  let queryStringList = [];

  for (const parameter of parameters) {
    if (parameter.in === "query") {
      // TODO: Extend this to include more types if needed
      if (parameter.schema.type === "array" && parameter.schema.items.enum) {
        const queryKey = parameter.name;
        const queryValue = getEnumValuesForRender(parameter.schema).join(",");
        queryStringList.push(`${queryKey}=${queryValue}`);
      }
    }
  }

  if (queryStringList.length > 0) {
    return `?${queryStringList.join("&")}`;
  }

  return "";
}
